import { useEffect } from "react";
import { Outlet, useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Sidebar } from "@/components/Sidebar.js";
import { useAuthStore } from "@/store/auth.js";
import { adminApi } from "@/lib/api.js";

export function AppLayout() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { setActiveTenantId, logout } = useAuthStore();
  const navigate = useNavigate();

  const { data: myTenants, isLoading } = useQuery({
    queryKey: ["my-tenants"],
    queryFn: () => adminApi.myTenants(),
    staleTime: 1000 * 60 * 5,
  });

  const activeTenant = myTenants?.find((t) => t.slug === tenantSlug);

  useEffect(() => {
    if (activeTenant) {
      setActiveTenantId(activeTenant.id);
    }
  }, [activeTenant?.id, setActiveTenantId]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!activeTenant) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-black/5 max-w-sm w-full mx-4">
          <p className="text-4xl mb-4">🔒</p>
          <h2 className="text-lg font-bold text-gray-900 mb-2">접근 권한이 없습니다</h2>
          <p className="text-sm text-gray-500 mb-6">
            이 사찰에 대한 관리 권한이 없거나 존재하지 않는 사찰입니다.
          </p>
          <div className="flex flex-col gap-2">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-600"
            >
              관리자 홈으로
            </Link>
            <button
              type="button"
              onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              로그아웃
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar myTenants={myTenants ?? []} activeTenantSlug={tenantSlug ?? ""} />
      <main className="flex flex-1 flex-col overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}

export function TenantRedirect() {
  const { data: myTenants, isLoading } = useQuery({
    queryKey: ["my-tenants"],
    queryFn: () => adminApi.myTenants(),
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!myTenants?.length) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-4xl mb-4">⛩</p>
          <h2 className="text-lg font-bold text-gray-900 mb-2">배정된 사찰이 없습니다</h2>
          <p className="text-sm text-gray-500">슈퍼관리자에게 사찰 배정을 요청하세요.</p>
        </div>
      </div>
    );
  }

  return <Navigate to={`/${myTenants[0].slug}/dashboard`} replace />;
}

export function InvalidUrlPage() {
  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-black/5 max-w-sm w-full mx-4">
        <p className="text-4xl mb-4">🔗</p>
        <h2 className="text-lg font-bold text-gray-900 mb-2">잘못된 URL입니다</h2>
        <p className="text-sm text-gray-500 mb-6">
          요청하신 페이지를 찾을 수 없습니다.
        </p>
        <Link
          to="/"
          className="inline-flex items-center rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-600"
        >
          관리자 홈으로
        </Link>
      </div>
    </div>
  );
}
